import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/**
 * Download filtered data as Excel file
 */
export const downloadExcel = (data, filename = "export.xlsx", columns = null) => {
  try {
    let exportData = data;
    
    if (columns) {
      exportData = data.map((item) => {
        const row = {};
        columns.forEach((col) => {
          row[col.header] = item[col.key];
        });
        return row;
      });
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    const maxWidth = 50;
    const colWidths = [];
    if (exportData.length > 0) {
      Object.keys(exportData[0]).forEach((key) => {
        const maxLen = Math.max(
          key.length,
          exportData.reduce((max, obj) => {
            const cellValue = obj[key] ? obj[key].toString() : "";
            return Math.max(max, cellValue.length);
          }, 0)
        );
        colWidths.push({ wch: Math.min(maxLen + 2, maxWidth) });
      });
    }
    ws["!cols"] = colWidths;

    XLSX.writeFile(wb, filename);
    return true;
  } catch (error) {
    console.error("Error downloading Excel:", error);
    throw error;
  }
};

/**
 * Download filtered data as PDF file - COLORS FIXED FOR READABILITY
 */
export const downloadPDF = (
  data,
  filename = "export.pdf",
  title = "Data Export",
  columns = null
) => {
  try {
    const doc = new jsPDF("l"); // Landscape
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Add title
    doc.setFontSize(14);
    doc.setTextColor(20, 20, 20); // Dark text
    doc.text(title, 14, 12);
    
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80); // Gray text
    doc.text(
      `Generated: ${new Date().toLocaleDateString()} | Total Records: ${data.length}`,
      14,
      18
    );

    // Prepare columns
    let tableColumns = [];
    if (columns && columns.length > 0) {
      tableColumns = columns.map((col) => ({
        header: col.header,
        dataKey: col.key || col.dataKey,
      }));
    } else if (data.length > 0) {
      const keys = Object.keys(data[0]);
      const excludeKeys = ["image", "password", "sync_status"];
      tableColumns = keys
        .filter((key) => !excludeKeys.includes(key))
        .slice(0, 12)
        .map((key) => ({
          header: key
            .replace(/_/g, " ")
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          dataKey: key,
        }));
    }

    // Prepare table data
    const tableData = data.map((item) => {
      const row = {};
      tableColumns.forEach((col) => {
        const key = col.dataKey;
        let value = item[key];

        if (!value) {
          value = "N/A";
        } else if (typeof value === "boolean") {
          value = value ? "Yes" : "No";
        } else if (typeof value === "object") {
          value = JSON.stringify(value);
        } else {
          value = value.toString();
        }

        if (value.length > 35) {
          value = value.substring(0, 32) + "...";
        }

        row[key] = value;
      });
      return row;
    });

    // Add table with FIXED COLORS
    doc.autoTable({
      columns: tableColumns,
      body: tableData,
      startY: 25,
      theme: "grid",
      styles: {
        cellPadding: 5,
        fontSize: 8,
        halign: "left",
        valign: "middle",
        overflow: "linebreak",
        textColor: [30, 30, 30], // ✅ DARK TEXT - READABLE
      },
      headStyles: {
        fillColor: [63, 81, 181], // ✅ Blue background (not too dark)
        textColor: [255, 255, 255], // ✅ White text on blue
        fontStyle: "bold",
        fontSize: 9,
        halign: "center",
        valign: "middle",
        cellPadding: 6,
      },
      bodyStyles: {
        // Alternate between white and very light blue
        fillColor: (row) => 
          row.index % 2 === 0 ? [255, 255, 255] : [240, 245, 255],
        textColor: [30, 30, 30], // ✅ Dark text always visible
      },
      columnStyles: {},
      margin: { top: 25, right: 12, bottom: 20, left: 12 },
      didDrawPage: (data) => {
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.getHeight();
        const pageWidth = pageSize.getWidth();
        const pageNum = doc.internal.pages.length - 1;
        const pageCount = doc.internal.pages.length - 1;

        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120); // Gray footer text
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      },
    });

    doc.save(filename);
    return true;
  } catch (error) {
    console.error("Error downloading PDF:", error);
    throw error;
  }
};

/**
 * Download filtered data as CSV file
 */
export const downloadCSV = (
  data,
  filename = "export.csv",
  columns = null,
  includeImages = false
) => {
  try {
    let exportData = data;
    let headers = [];

    if (columns) {
      headers = columns.map((col) => col.header);
      exportData = data.map((item) => {
        const row = [];
        columns.forEach((col) => {
          let value = item[col.key];
          
          if (includeImages && col.key === "image") {
            value = value && value.trim() !== "" ? "Available" : "Not Available";
          }
          
          row.push(value ? `"${value.toString().replace(/"/g, '""')}"` : '""');
        });
        return row.join(",");
      });
    } else {
      if (exportData.length > 0) {
        headers = Object.keys(exportData[0]);
        exportData = exportData.map((item) => {
          const row = [];
          headers.forEach((key) => {
            let value = item[key];
            
            if (includeImages && key === "image") {
              value = value && value.trim() !== "" ? "Available" : "Not Available";
            }
            
            row.push(value ? `"${value.toString().replace(/"/g, '""')}"` : '""');
          });
          return row.join(",");
        });
      }
    }

    const csvContent = [
      headers.map((h) => `"${h}"`).join(","),
      ...exportData,
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  } catch (error) {
    console.error("Error downloading CSV:", error);
    throw error;
  }
};

/**
 * Member-specific export function (formerly Student)
 * 
 * ✅ UPDATED: Shows member fields instead of student fields
 * Field Mapping:
 * - gender → displayed from hostel_name OR gender field
 * - gym_location → displayed from boarding_type OR gym_location field
 * - membership_start_date → displayed from father_name OR membership_start_date field
 * - membership_end_date → displayed from mother_name OR membership_end_date field
 */
export const downloadStudentData = (students, format = "excel") => {
  const filename = `Members_${new Date().toISOString().split("T")[0]}`;
  
  // ✅ Transform data to show member fields correctly
  const transformedStudents = students.map(student => ({
    ...student,
    gender: student.gender || student.hostel_name || "",
    gym_location: student.gym_location || student.boarding_type || "",
    membership_start_date: student.membership_start_date || student.father_name || "",
    membership_end_date: student.membership_end_date || student.mother_name || "",
  }));
  
  const columns = [
    { header: "Member ID", key: "student_id" },
    { header: "First Name", key: "first_name" },
    { header: "Last Name", key: "last_name" },
    { header: "Email", key: "email" },
    { header: "Contact No", key: "mobile_no" },
    { header: "Gender", key: "gender" },
    { header: "GYM Location", key: "gym_location" },
    { header: "Membership Start", key: "membership_start_date" },
    { header: "Membership End", key: "membership_end_date" },
    { header: "Registration Date", key: "created_at" },
    { header: "Photo Status", key: "image" },
  ];

  try {
    switch (format.toLowerCase()) {
      case "excel":
        return downloadExcel(
          transformedStudents,
          `${filename}.xlsx`,
          columns.filter((col) => col.key !== "image")
        );
      case "pdf":
        return downloadPDF(
          transformedStudents,
          `${filename}.pdf`,
          "Member Registration Report",
          columns.filter((col) => col.key !== "image")
        );
      case "csv":
        return downloadCSV(
          transformedStudents,
          `${filename}.csv`,
          columns,
          true
        );
      default:
        throw new Error("Invalid format");
    }
  } catch (error) {
    console.error(`Error downloading member data as ${format}:`, error);
    throw error;
  }
};

/**
 * Staff-specific export function
 */
export const downloadStaffData = (staffList, format = "excel") => {
  const filename = `Staff_${new Date().toISOString().split("T")[0]}`;
  
  const columns = [
    { header: "Staff ID", key: "staff_id" },
    { header: "First Name", key: "first_name" },
    { header: "Last Name", key: "last_name" },
    { header: "Department", key: "department" },
    { header: "Email", key: "email" },
    { header: "Mobile No", key: "mobile_no" },
    { header: "Photo Status", key: "image" },
  ];

  try {
    switch (format.toLowerCase()) {
      case "excel":
        return downloadExcel(
          staffList,
          `${filename}.xlsx`,
          columns.filter((col) => col.key !== "image")
        );
      case "pdf":
        return downloadPDF(
          staffList,
          `${filename}.pdf`,
          "Staff Registration Report",
          columns.filter((col) => col.key !== "image")
        );
      case "csv":
        return downloadCSV(
          staffList,
          `${filename}.csv`,
          columns,
          true
        );
      default:
        throw new Error("Invalid format");
    }
  } catch (error) {
    console.error(`Error downloading staff data as ${format}:`, error);
    throw error;
  }
};